'use client';

import { BUTTON_LABELS, CERTIFICATE_LABELS, WORDS } from '@/constants';
import { Button, CertificationCertificate, PrintButton, TextContent } from '@/components';
import { capitalize } from 'lodash';
import { extractRouteParams } from '@/domains/routing';
import { useAppLoading, useSimulationRouting } from '@/hooks';
import { formatDate } from '@/utils';
import { useCurrentUser, useGlobalStore } from '@/store';
import { useCurrentUserFeature } from '@/features/users/currentUser/userCurrentUserFeature';
import { useParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef } from 'react';

/** Props for the CertificationCertificateView component */
interface CertificationCertificateViewProps {
  /**
   * Optional override for the recipient name displayed on the certificate.
   * When provided (e.g. in the manager dashboard context), this name is used
   * instead of the logged-in user's name. Falls back to the current user's
   * full name if not specified.
   */
  recipientName?: string;

  /**
   * Optional override for the date completed displayed on the certificate.
   * When provided (e.g. in the manager dashboard context), this date is used
   * instead of the date from the active certificate. Falls back to the active
   * certificate's completion date if not specified.
   */
  dateCompleted?: string;

  /**
   * Whether to show the header section of the certificate view, which
   * includes the title and close button.
   */
  showHeader?: boolean;

  /**
   * Whether to show the introductory congratulatory text above the certificate.
   * This text congratulates the user on earning the certification and provides
   * context about the achievement.
   */
  showIntroText?: boolean;
}

/**
 * CertificationCertificateView Component
 *
 * This component is responsible for displaying a certification certificate,
 * including the recipient's name, the date of completion, and the certification
 * title. It also provides functionality for printing the certificate and
 * navigating back to the dashboard. The component is designed to be flexible,
 * allowing for overrides of the recipient name and completion date, as well as
 * options to show or hide the header and introductory text based on the context
 * in which it is used (e.g. manager dashboard vs. sales representative view).
 */
export const CertificationCertificateView: React.FC<CertificationCertificateViewProps> = ({
  recipientName: recipientNameProp,
  dateCompleted: dateCompletedProp,
  showHeader = true,
  showIntroText = true,
}) => {
  // Hooks and state
  const currentUser = useCurrentUser();
  const isLoading = useAppLoading();
  const printRef = useRef<HTMLDivElement>(null);
  const { certificationId: certIdFromRoute } = extractRouteParams(useParams());
  const { navigateToDashboard } = useSimulationRouting();
  const { activeCertificate, certifications, isPrinting, setActiveCertificate } = useGlobalStore();
  const { currentUserFullName } = useCurrentUserFeature({ currentUser });

  /**
   * Memoized value for the current user's full name, used for display on the certificate and in
   * the print title. This value is derived from the currentUser object in the global store and is
   * recomputed whenever the currentUser changes.
   */
  const userName = useMemo(() => {
    if (recipientNameProp) return recipientNameProp;
    return currentUserFullName;
  }, [currentUserFullName, recipientNameProp]);

  /**
   * Determine the date to display on the certificate. This uses the dateCompletedProp if provided
   * (e.g. from the manager dashboard), otherwise it falls back to the completedAt date of the active
   * certificate's simulation, formatted as "MMMM YYYY". This allows for flexibility in displaying the
   * completion date based on the context in which the certificate view is used.
   */
  const dateCompleted = useMemo(() => {
    if (dateCompletedProp) return dateCompletedProp;
    return formatDate(activeCertificate?.certificationSimulation?.completedAt, 'monthYear');
  }, [dateCompletedProp, activeCertificate]);

  /**
   * Memoized values for the certification title and short title, derived from the active certificate.
   */
  const [certTitle, certTitleShort] = useMemo(() => {
    return [activeCertificate?.title || '', activeCertificate?.titleShort || ''];
  }, [activeCertificate]);

  /**
   * Memoized value for the page subtitle, which incorporates the certification title.
   */
  const pageSubtitle = useMemo(() => {
    const replaceTarget = certTitleShort ? certTitleShort : certTitle;
    return CERTIFICATE_LABELS.PAGE_SUBTITLE.replace('%CERTIFICATION_TITLE%', replaceTarget);
  }, [certTitle, certTitleShort]);

  /**
   * Memoized value for the print title, which combines the certificate label, certification title,
   * and user name. This title is used for the print document and provides a clear and descriptive
   * title for the certificate when printed.
   */
  const printTitle = useMemo(() => {
    return `${CERTIFICATE_LABELS.CERTIFICATION} | ${certTitle}  —  ${userName}`;
  }, [certTitle, userName]);

  /**
   * On component mount, find the certification that matches the certificationId
   * from the route parameters and set it as the active certificate in the global store.
   * This allows the rest of the component to access the active certificate's details
   * for display and printing. If no matching certification is found, a warning is logged.
   */
  useEffect(() => {
    const certificationId = certIdFromRoute || activeCertificate?.id;
    const certification = certifications.find((c) => c.id === Number(certificationId));
    if (certification) setActiveCertificate(certification);
  }, [activeCertificate?.id, certIdFromRoute, certifications, setActiveCertificate]);

  // =============================================================
  // Render
  // =============================================================
  return (
    <div className="w-full">
      {showHeader && (
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center justify-start">
            <TextContent
              color="neutral-black"
              label={CERTIFICATE_LABELS.CERTIFICATION}
              level={3}
              type="headline"
              variant="sectionTitle"
            />
            <span className="mx-6 inline-block h-10 border-r border-neutral-gray-500" />
            <TextContent
              color="neutral-gray-700"
              isLoading={isLoading}
              label={certTitleShort ? certTitleShort : certTitle}
              level={3}
              loadingClassName="w-56"
              testId="certification-title"
              type="headline"
              variant="sectionTitle"
            />
          </div>
          <div className="flex items-center justify-center">
            <Button
              className="flex items-center gap-4 text-neutral-black"
              iconLeft="X"
              label={BUTTON_LABELS.CLOSE}
              onClick={() => navigateToDashboard()}
              variant="tertiary"
            />
          </div>
        </div>
      )}

      <div className="w-full max-w-4xl">
        {showIntroText && (
          <div>
            <TextContent
              className="mb-4"
              color="neutral-black"
              label={`${capitalize(WORDS.CONGRATS)}!`}
              level={1}
              testId="page-title"
              type="headline"
              variant="pageTitle"
            />

            <TextContent
              className="mb-7"
              color="neutral-black"
              isLoading={isLoading}
              label={pageSubtitle}
              loadingClassName="w-full mb-0.5"
              loadingLines={3}
              testId="congrats-text"
              type="paragraph"
              weight="bold"
            />
          </div>
        )}

        <div className="w-full" ref={printRef}>
          <CertificationCertificate
            certificationName={certTitle}
            completionDate={dateCompleted}
            isLoading={isLoading}
            isPrinting={isPrinting}
            issuingTeam={activeCertificate?.issuingTeam}
            recipientName={userName}
          />
        </div>

        <div className="mt-7 flex w-full justify-center">
          <PrintButton
            buttonLabel={BUTTON_LABELS.PRINT_CERTIFICATE}
            buttonSize="medium"
            contentRef={printRef}
            documentTitle={printTitle}
            pageStyle={`@page { size: landscape; margin: 20mm; padding: 10mm 20mm; }`}
          />
        </div>

        <div className="h-[100px] w-full" />
      </div>
    </div>
  );
};

CertificationCertificateView.displayName = 'CertificationCertificateView';

export default CertificationCertificateView;
